import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Trash2,
  Pencil,
  Save,
  Star,
  Tag,
  Link2,
  Search,
  RefreshCw,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Check,
} from 'lucide-react';

import {
  fetchMeetingNotesThunk,
  createMeetingNoteThunk,
  updateMeetingNoteThunk,
  deleteMeetingNoteThunk,
} from '../../../redux/admin/meetingNotesActionThunks';
import { fetchRoadmapItemsThunk } from '../../../redux/admin/roadmapActionThunks';
import { createTaskThunk } from '../../../redux/admin/tasksActionThunks';

const SEVERITY_COLORS = {
  critical: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30' },
  high: { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/30' },
  medium: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30' },
  low: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/30' },
};

const EMPTY_FORM = { title: '', body: '', tags: '', is_flagged: false, roadmap_item_ids: [] };

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

/**
 * Admin meeting-notes / decision log -- sits next to the roadmap tracker
 * (Platform Status tab) rather than duplicating it: a note records what was
 * discussed and decided, and can link to the real, live roadmap items it
 * affects, so a note is never disconnected from the actual open work it's
 * about. Built 2026-07-31 after a live legal/security review of the
 * platform, seeded with that review as the first real note.
 */
const AdminMeetingNotesComponent = ({ darkMode = true }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const notes = useSelector((s) => s.meetingNotes.notes);
  const loading = useSelector((s) => s.meetingNotes.loading.notes);
  const mutating = useSelector((s) => s.meetingNotes.loading.mutate);
  const error = useSelector((s) => s.meetingNotes.errors.notes);
  const roadmapItems = useSelector((s) => s.roadmap.items);

  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newNote, setNewNote] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [expandedId, setExpandedId] = useState(null);

  const themeClasses = {
    text: darkMode ? 'text-white' : 'text-gray-900',
    textSecondary: darkMode ? 'text-gray-300' : 'text-gray-600',
    textMuted: darkMode ? 'text-gray-400' : 'text-gray-500',
    border: darkMode ? 'border-gray-700' : 'border-gray-200',
    hoverBg: darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50',
    inputBg: darkMode ? 'bg-gray-700' : 'bg-white',
    inputBorder: darkMode ? 'border-gray-600' : 'border-gray-300',
    buttonPrimary: 'bg-blue-600 hover:bg-blue-700 text-white',
    buttonSecondary: darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700',
  };

  useEffect(() => {
    dispatch(fetchMeetingNotesThunk({ flaggedOnly, search: search || undefined }));
  }, [dispatch, flaggedOnly, search]);

  useEffect(() => {
    if (!roadmapItems?.length) dispatch(fetchRoadmapItemsThunk({}));
  }, [dispatch, roadmapItems?.length]);

  const parseTags = (raw) => raw.split(',').map((s) => s.trim()).filter(Boolean);

  const handleCreate = (e) => {
    e.preventDefault();
    dispatch(createMeetingNoteThunk({
      title: newNote.title,
      body: newNote.body,
      is_flagged: newNote.is_flagged,
      tags: parseTags(newNote.tags),
      roadmap_item_ids: newNote.roadmap_item_ids,
    })).then((res) => {
      if (!res.error) {
        setNewNote(EMPTY_FORM);
        setShowCreate(false);
      }
    });
  };

  const startEdit = (note) => {
    setEditingId(note.id);
    setEditForm({
      title: note.title,
      body: note.body,
      tags: (note.tags || []).join(', '),
      is_flagged: note.is_flagged,
      roadmap_item_ids: (note.roadmap_items || []).map((r) => r.id),
    });
  };

  const saveEdit = (noteId) => {
    dispatch(updateMeetingNoteThunk({
      noteId,
      updates: {
        title: editForm.title,
        body: editForm.body,
        is_flagged: editForm.is_flagged,
        tags: parseTags(editForm.tags),
        roadmap_item_ids: editForm.roadmap_item_ids,
      },
    })).then((res) => { if (!res.error) setEditingId(null); });
  };

  const toggleFlag = (note) => {
    dispatch(updateMeetingNoteThunk({ noteId: note.id, updates: { is_flagged: !note.is_flagged } }));
  };

  const handleDelete = (noteId) => {
    if (window.confirm(t('admin.meetingNotes.confirmDelete'))) {
      dispatch(deleteMeetingNoteThunk(noteId));
    }
  };

  const [taskCreatedIds, setTaskCreatedIds] = useState({});
  const handleCreateTask = (note) => {
    dispatch(createTaskThunk({
      title: `Action item: ${note.title}`,
      description: note.body,
      priority: 'medium',
      assignee_ids: [],
      source_type: 'meeting_note',
      source_id: note.id,
    })).then((res) => {
      if (!res.error) setTaskCreatedIds((s) => ({ ...s, [note.id]: true }));
    });
  };

  const toggleRoadmapId = (list, id) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  const RoadmapPicker = ({ selectedIds, onChange }) => (
    <div className={`max-h-40 overflow-y-auto rounded-lg border ${themeClasses.inputBorder} p-2 space-y-1`}>
      {(roadmapItems || []).length === 0 && (
        <p className={`text-xs ${themeClasses.textMuted} p-2`}>{t('admin.meetingNotes.noRoadmapItems')}</p>
      )}
      {(roadmapItems || []).map((r) => (
        <label key={r.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm cursor-pointer ${themeClasses.hoverBg}`}>
          <input
            type="checkbox"
            checked={selectedIds.includes(r.id)}
            onChange={() => onChange(toggleRoadmapId(selectedIds, r.id))}
            className="flex-shrink-0"
          />
          <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full border flex-shrink-0 ${(SEVERITY_COLORS[r.severity] || SEVERITY_COLORS.medium).bg} ${(SEVERITY_COLORS[r.severity] || SEVERITY_COLORS.medium).text} ${(SEVERITY_COLORS[r.severity] || SEVERITY_COLORS.medium).border}`}>
            {r.severity}
          </span>
          <span className={`${themeClasses.text} truncate`}>{r.title}</span>
        </label>
      ))}
    </div>
  );

  const RoadmapChips = ({ items }) => {
    if (!items?.length) return null;
    return (
      <div className="flex flex-wrap gap-1.5 mt-2">
        {items.map((r) => {
          const colors = SEVERITY_COLORS[r.severity] || SEVERITY_COLORS.medium;
          return (
            <span key={r.id} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${colors.bg} ${colors.text} ${colors.border}`}>
              <Link2 className="h-3 w-3" /> {r.title}
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className={`text-lg font-semibold ${themeClasses.text}`}>{t('admin.meetingNotes.title')}</h3>
          <p className={`text-sm ${themeClasses.textMuted}`}>{t('admin.meetingNotes.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 ${themeClasses.textMuted}`} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('admin.meetingNotes.searchPlaceholder')}
              className={`pl-8 pr-2.5 py-1.5 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`}
            />
          </div>
          <button
            onClick={() => setFlaggedOnly((f) => !f)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border ${
              flaggedOnly ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' : `${themeClasses.buttonSecondary} border-transparent`
            }`}
          >
            <Star className={`h-4 w-4 ${flaggedOnly ? 'fill-amber-400' : ''}`} /> {t('admin.meetingNotes.flaggedOnly')}
          </button>
          {loading && <RefreshCw className={`h-4 w-4 animate-spin ${themeClasses.textMuted}`} />}
          <button
            onClick={() => setShowCreate((s) => !s)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${themeClasses.buttonPrimary}`}
          >
            <Plus className="h-4 w-4" /> {t('admin.meetingNotes.newNote')}
          </button>
        </div>
      </div>

      {error && (
        <div className={`flex items-center gap-2 p-3 rounded-lg ${darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700'}`}>
          <AlertTriangle className="h-4 w-4 flex-shrink-0" /> {error}
        </div>
      )}

      {showCreate && (
        <form onSubmit={handleCreate} className={`p-4 rounded-lg border ${themeClasses.border} space-y-3`}>
          <input
            type="text"
            placeholder={t('admin.meetingNotes.titlePlaceholder')}
            value={newNote.title}
            onChange={(e) => setNewNote((f) => ({ ...f, title: e.target.value }))}
            className={`w-full px-3 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`}
            required
          />
          <textarea
            placeholder={t('admin.meetingNotes.bodyPlaceholder')}
            value={newNote.body}
            onChange={(e) => setNewNote((f) => ({ ...f, body: e.target.value }))}
            className={`w-full px-3 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm resize-y`}
            rows={5}
            required
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              type="text"
              placeholder={t('admin.meetingNotes.tagsPlaceholder')}
              value={newNote.tags}
              onChange={(e) => setNewNote((f) => ({ ...f, tags: e.target.value }))}
              className={`px-2.5 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`}
            />
            <label className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border ${themeClasses.inputBorder} text-sm ${themeClasses.text} cursor-pointer`}>
              <input type="checkbox" checked={newNote.is_flagged} onChange={(e) => setNewNote((f) => ({ ...f, is_flagged: e.target.checked }))} />
              <Star className="h-4 w-4 text-amber-400" /> {t('admin.meetingNotes.flagThisNote')}
            </label>
          </div>
          <div>
            <p className={`text-xs font-medium mb-1.5 flex items-center gap-1 ${themeClasses.textMuted}`}>
              <Link2 className="h-3.5 w-3.5" /> {t('admin.meetingNotes.linkRoadmapItems')}
            </p>
            <RoadmapPicker selectedIds={newNote.roadmap_item_ids} onChange={(ids) => setNewNote((f) => ({ ...f, roadmap_item_ids: ids }))} />
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={mutating} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${themeClasses.buttonPrimary} disabled:opacity-50`}>
              <Save className="h-4 w-4" /> {t('admin.meetingNotes.save')}
            </button>
            <button type="button" onClick={() => { setShowCreate(false); setNewNote(EMPTY_FORM); }} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${themeClasses.buttonSecondary}`}>
              {t('admin.meetingNotes.cancel')}
            </button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {(notes || []).length === 0 && !loading && (
          <p className={`text-sm ${themeClasses.textMuted} py-6 text-center`}>{t('admin.meetingNotes.noNotesMatch')}</p>
        )}
        {(notes || []).map((note) => {
          const isEditing = editingId === note.id;
          const isExpanded = expandedId === note.id;

          if (isEditing) {
            return (
              <div key={note.id} className={`p-4 rounded-lg border ${themeClasses.border} space-y-3`}>
                <input type="text" value={editForm.title} onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                  className={`w-full px-3 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`} />
                <textarea value={editForm.body} onChange={(e) => setEditForm((f) => ({ ...f, body: e.target.value }))}
                  className={`w-full px-3 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm resize-y`} rows={6} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input type="text" value={editForm.tags} onChange={(e) => setEditForm((f) => ({ ...f, tags: e.target.value }))}
                    placeholder={t('admin.meetingNotes.tagsPlaceholder')}
                    className={`px-2.5 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`} />
                  <label className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border ${themeClasses.inputBorder} text-sm ${themeClasses.text} cursor-pointer`}>
                    <input type="checkbox" checked={editForm.is_flagged} onChange={(e) => setEditForm((f) => ({ ...f, is_flagged: e.target.checked }))} />
                    <Star className="h-4 w-4 text-amber-400" /> {t('admin.meetingNotes.flagThisNote')}
                  </label>
                </div>
                <div>
                  <p className={`text-xs font-medium mb-1.5 flex items-center gap-1 ${themeClasses.textMuted}`}>
                    <Link2 className="h-3.5 w-3.5" /> {t('admin.meetingNotes.linkRoadmapItems')}
                  </p>
                  <RoadmapPicker selectedIds={editForm.roadmap_item_ids} onChange={(ids) => setEditForm((f) => ({ ...f, roadmap_item_ids: ids }))} />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => saveEdit(note.id)} disabled={mutating} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${themeClasses.buttonPrimary} disabled:opacity-50`}>
                    <Save className="h-4 w-4" /> {t('admin.meetingNotes.save')}
                  </button>
                  <button onClick={() => setEditingId(null)} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${themeClasses.buttonSecondary}`}>
                    {t('admin.meetingNotes.cancel')}
                  </button>
                </div>
              </div>
            );
          }

          return (
            <div key={note.id} className={`p-4 rounded-lg border ${note.is_flagged ? 'border-amber-500/40' : themeClasses.border} ${themeClasses.hoverBg} transition-colors`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : note.id)}>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {note.is_flagged && <Star className="h-4 w-4 text-amber-400 fill-amber-400 flex-shrink-0" />}
                    <span className={`font-medium ${themeClasses.text}`}>{note.title}</span>
                    {isExpanded ? <ChevronUp className={`h-4 w-4 ${themeClasses.textMuted}`} /> : <ChevronDown className={`h-4 w-4 ${themeClasses.textMuted}`} />}
                  </div>
                  <p className={`text-xs ${themeClasses.textMuted}`}>
                    {note.author_name || t('admin.meetingNotes.unknownAuthor')} · {formatDate(note.created_at)}
                  </p>
                  {note.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {note.tags.map((tag) => (
                        <span key={tag} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                          <Tag className="h-3 w-3" /> {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <RoadmapChips items={note.roadmap_items} />
                  {isExpanded && (
                    <p className={`text-sm ${themeClasses.textSecondary} mt-3 whitespace-pre-wrap leading-relaxed`}>
                      {note.body}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button onClick={() => toggleFlag(note)} aria-label={t('admin.meetingNotes.toggleFlagAria', { title: note.title })}
                    className={`p-1.5 rounded-lg ${note.is_flagged ? 'text-amber-400 hover:bg-amber-900/20' : `${themeClasses.textMuted} hover:text-amber-400 ${darkMode ? 'hover:bg-amber-900/20' : 'hover:bg-amber-50'}`}`}>
                    <Star className={`h-4 w-4 ${note.is_flagged ? 'fill-amber-400' : ''}`} />
                  </button>
                  <button
                    onClick={() => handleCreateTask(note)}
                    disabled={!!taskCreatedIds[note.id]}
                    aria-label={t('admin.meetingNotes.createTaskAria', { title: note.title })}
                    title={t('admin.meetingNotes.createTask')}
                    className={`p-1.5 rounded-lg disabled:opacity-50 ${darkMode ? 'text-gray-500 hover:text-indigo-400 hover:bg-indigo-900/20' : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'}`}
                  >
                    {taskCreatedIds[note.id] ? <Check className="h-4 w-4 text-emerald-500" /> : <ClipboardList className="h-4 w-4" />}
                  </button>
                  <button onClick={() => startEdit(note)} aria-label={t('admin.meetingNotes.editAria', { title: note.title })}
                    className={`p-1.5 rounded-lg ${darkMode ? 'text-gray-500 hover:text-blue-400 hover:bg-blue-900/20' : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'}`}>
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => handleDelete(note.id)} aria-label={t('admin.meetingNotes.deleteAria', { title: note.title })}
                    className={`p-1.5 rounded-lg ${darkMode ? 'text-gray-500 hover:text-red-400 hover:bg-red-900/20' : 'text-gray-400 hover:text-red-600 hover:bg-red-50'}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminMeetingNotesComponent;
